/**
 * MagicEvaluator - 魔法カード評価
 *
 * 魔法カードをシミュレートして、使用価値をスコア化。
 * 使用前後のスコア差からSPコストを引いて価値を算出。
 */

import type { GameState } from '../../gameEngine/GameState';
import type { CardWithId } from '../../../types/card';
import type { PlayerNumber } from '../../../types/player';
import { evaluateBoard, type EvaluationWeights, DEFAULT_WEIGHTS } from './boardEvaluator';
import { simulateMagicUse } from './actionSimulator';

// =============================================================================
// 型定義
// =============================================================================

/** 魔法評価設定 */
export interface MagicEvaluationConfig {
  /** SP1あたりのペナルティ */
  spCostPenalty: number;
  /** 使用する最低価値（閾値） */
  minValueThreshold: number;
}

/** 魔法評価結果 */
export interface MagicEvaluationResult {
  /** 評価対象カード */
  card: CardWithId;
  /** 計算された価値 */
  value: number;
  /** 使用前スコア */
  beforeScore: number;
  /** 使用後スコア */
  afterScore: number;
  /** スコア差分 */
  scoreDiff: number;
  /** SPコストペナルティ */
  costPenalty: number;
  /** シミュレーション成功フラグ */
  simulationSuccess: boolean;
  /** エラーメッセージ（失敗時） */
  error?: string;
}

// =============================================================================
// デフォルト値
// =============================================================================

export const DEFAULT_MAGIC_CONFIG: MagicEvaluationConfig = {
  spCostPenalty: 42.15,
  minValueThreshold: 16.05,
};

// =============================================================================
// パラメータ範囲（GA用）
// =============================================================================

export interface MagicConfigRange {
  min: number;
  max: number;
}

export const MAGIC_CONFIG_RANGES: Record<keyof MagicEvaluationConfig, MagicConfigRange> = {
  spCostPenalty: { min: 0, max: 50 },
  minValueThreshold: { min: -100, max: 100 },
};

// =============================================================================
// 評価関数
// =============================================================================

/**
 * 魔法カードの使用価値を評価
 *
 * @param state - 現在のゲーム状態
 * @param card - 評価する魔法カード
 * @param player - 使用プレイヤー
 * @param weights - 盤面評価パラメータ
 * @param config - 魔法評価設定
 * @returns 評価結果
 */
export function evaluateMagicCard(
  state: GameState,
  card: CardWithId,
  player: PlayerNumber,
  weights: EvaluationWeights = DEFAULT_WEIGHTS,
  config: MagicEvaluationConfig = DEFAULT_MAGIC_CONFIG
): MagicEvaluationResult {
  // 1. 現在の盤面スコア
  const beforeScore = evaluateBoard(state, player, weights);

  // 2. 魔法使用をシミュレート
  const simulationResult = simulateMagicUse(state, card, player);

  // シミュレーション失敗の場合
  if (!simulationResult.success || !simulationResult.state) {
    return {
      card,
      value: -Infinity,
      beforeScore,
      afterScore: beforeScore,
      scoreDiff: 0,
      costPenalty: 0,
      simulationSuccess: false,
      error: simulationResult.error,
    };
  }

  // 3. 使用後の盤面スコア
  const afterScore = evaluateBoard(simulationResult.state, player, weights);

  // 4. 価値計算
  const scoreDiff = afterScore - beforeScore;
  const costPenalty = (card.cost || 0) * config.spCostPenalty;
  const value = scoreDiff - costPenalty;

  return {
    card,
    value,
    beforeScore,
    afterScore,
    scoreDiff,
    costPenalty,
    simulationSuccess: true,
  };
}

/**
 * 複数の魔法カードを評価し、結果をソート
 *
 * @param cards - 評価する魔法カード配列
 * @param state - 現在のゲーム状態
 * @param player - 使用プレイヤー
 * @param weights - 盤面評価パラメータ
 * @param config - 魔法評価設定
 * @returns ソート済みの評価結果配列（価値の高い順）
 */
export function evaluateMagicCards(
  cards: CardWithId[],
  state: GameState,
  player: PlayerNumber,
  weights: EvaluationWeights = DEFAULT_WEIGHTS,
  config: MagicEvaluationConfig = DEFAULT_MAGIC_CONFIG
): MagicEvaluationResult[] {
  const results: MagicEvaluationResult[] = [];

  for (const card of cards) {
    const result = evaluateMagicCard(state, card, player, weights, config);
    results.push(result);
  }

  // 価値の高い順にソート
  results.sort((a, b) => b.value - a.value);

  return results;
}

/**
 * 最適な魔法カードを選択
 *
 * @param usableMagicCards - 使用可能な魔法カード配列
 * @param state - 現在のゲーム状態
 * @param player - 使用プレイヤー
 * @param weights - 盤面評価パラメータ
 * @param config - 魔法評価設定
 * @returns 最適なカード（使用価値がない場合はnull）
 */
export function chooseBestMagicCard(
  usableMagicCards: CardWithId[],
  state: GameState,
  player: PlayerNumber,
  weights: EvaluationWeights = DEFAULT_WEIGHTS,
  config: MagicEvaluationConfig = DEFAULT_MAGIC_CONFIG
): CardWithId | null {
  if (usableMagicCards.length === 0) return null;

  let bestCard: CardWithId | null = null;
  let bestValue = config.minValueThreshold;

  for (const card of usableMagicCards) {
    const result = evaluateMagicCard(state, card, player, weights, config);

    if (result.simulationSuccess && result.value > bestValue) {
      bestValue = result.value;
      bestCard = card;
    }
  }

  return bestCard;
}

/**
 * 魔法カードを使用すべきかどうかを判断
 *
 * @param card - 判断する魔法カード
 * @param state - 現在のゲーム状態
 * @param player - 使用プレイヤー
 * @param weights - 盤面評価パラメータ
 * @param config - 魔法評価設定
 * @returns 使用すべきならtrue
 */
export function shouldUseMagicCard(
  card: CardWithId,
  state: GameState,
  player: PlayerNumber,
  weights: EvaluationWeights = DEFAULT_WEIGHTS,
  config: MagicEvaluationConfig = DEFAULT_MAGIC_CONFIG
): boolean {
  const result = evaluateMagicCard(state, card, player, weights, config);
  return result.simulationSuccess && result.value > config.minValueThreshold;
}

/**
 * ランダムな魔法評価設定を生成（GA用）
 */
export function createRandomMagicConfig(): MagicEvaluationConfig {
  const config: Partial<MagicEvaluationConfig> = {};
  const configRecord = config as unknown as Record<string, number>;

  for (const [key, range] of Object.entries(MAGIC_CONFIG_RANGES)) {
    const value = range.min + Math.random() * (range.max - range.min);
    configRecord[key] = value;
  }

  return config as MagicEvaluationConfig;
}

/**
 * 魔法評価設定をクローン
 */
export function cloneMagicConfig(config: MagicEvaluationConfig): MagicEvaluationConfig {
  return { ...config };
}

/**
 * 魔法評価設定を正規化（範囲外の値を修正）
 */
export function normalizeMagicConfig(config: MagicEvaluationConfig): MagicEvaluationConfig {
  const normalized = cloneMagicConfig(config);
  const normalizedRecord = normalized as unknown as Record<string, number>;

  for (const [key, range] of Object.entries(MAGIC_CONFIG_RANGES)) {
    const currentValue = normalizedRecord[key];
    normalizedRecord[key] = Math.max(range.min, Math.min(range.max, currentValue));
  }

  return normalized;
}
